import './CreateQuoteForm.css';
import React, { useState, useEffect } from 'react';
import { fetchAPI } from '../../utils/utils';
import { hasFamiliesFeature } from '../../utils/colla-features';
import { formatMoney } from '../../utils/money';

function CreateQuoteForm({
  form,
  onChange,
  onSubmit,
  getAvailableMonths,
  monthNames,
  getPeriodLabel,
  createdQuote,
  onSendNotification,
  onDismissSuccess,
  sendingNotification,
  stripeConnectStatus,
}) {
  // Step management - dynamic based on Stripe status
  const [currentStep, setCurrentStep] = useState(1);
  // Steps: 1. Tipus, 2. Preu, 3. Voluntària, 4. Obertura, 5. Límit, 6. Comissió (only if Stripe verified)
  const totalSteps = stripeConnectStatus?.verified ? 6 : 5;

  // Local state for variants
  const [variants, setVariants] = useState([]);
  const [showAddVariantForm, setShowAddVariantForm] = useState(false);
  const [editingVariantIndex, setEditingVariantIndex] = useState(null);
  const [newVariant, setNewVariant] = useState({
    nom: '',
    descripcio: '',
    import: '',
    has_children_addon: false,
    children_price: '',
    max_children: '',
    has_adults_addon: false,
    adult_price: '',
    max_adults: ''
  });
  const [variantValidationErrors, setVariantValidationErrors] = useState({});
  const [familyExamples, setFamilyExamples] = useState([]);
  const [loadingFamilies, setLoadingFamilies] = useState(false);

  // Validation
  const validateVariant = (variant) => {
    const errors = {};
    if (!variant.nom || variant.nom.trim() === '') {
      errors.nom = 'El nom de la variant és obligatori';
    }
    if (variant.import === '' || variant.import === null || variant.import === undefined) {
      errors.import = 'L\'import és obligatori';
    } else {
      const importValue = parseFloat(variant.import);
      if (isNaN(importValue)) {
        errors.import = 'L\'import ha de ser un número vàlid';
      } else if (importValue < 0) {
        errors.import = 'L\'import no pot ser negatiu';
      }
    }
    if (variant.has_children_addon) {
      if (variant.children_price === '' || variant.children_price === null || variant.children_price === undefined) {
        errors.children_price = 'El preu per infant és obligatori';
      } else {
        const childrenPrice = parseFloat(variant.children_price);
        if (isNaN(childrenPrice)) {
          errors.children_price = 'El preu per infant ha de ser un número vàlid';
        } else if (childrenPrice < 0) {
          errors.children_price = 'El preu per infant no pot ser negatiu';
        }
      }
      if (variant.max_children !== '' && variant.max_children !== null && variant.max_children !== undefined) {
        const maxChildren = parseInt(variant.max_children);
        if (isNaN(maxChildren)) {
          errors.max_children = 'El límit ha de ser un número enter';
        } else if (maxChildren < 1) {
          errors.max_children = 'El límit mínim és 1';
        }
      }
    }
    if (variant.has_adults_addon) {
      if (variant.adult_price === '' || variant.adult_price === null || variant.adult_price === undefined) {
        errors.adult_price = 'El preu per adult és obligatori';
      } else {
        const adultPrice = parseFloat(variant.adult_price);
        if (isNaN(adultPrice)) {
          errors.adult_price = 'El preu per adult ha de ser un número vàlid';
        } else if (adultPrice < 0) {
          errors.adult_price = 'El preu per adult no pot ser negatiu';
        }
      }
      if (variant.max_adults !== '' && variant.max_adults !== null && variant.max_adults !== undefined) {
        const maxAdults = parseInt(variant.max_adults);
        if (isNaN(maxAdults)) {
          errors.max_adults = 'El límit ha de ser un número enter';
        } else if (maxAdults < 1) {
          errors.max_adults = 'El límit mínim és 1';
        }
      }
    }
    return errors;
  };

  // Fetch family examples
  useEffect(() => {
    // Skip fetching for university colles
    if (!hasFamiliesFeature()) {
      setFamilyExamples([]);
      return;
    }

    const hasFamilyQuota = variants.some(v => v.has_children_addon || v.has_adults_addon);
    if (hasFamilyQuota && !loadingFamilies) {
      setLoadingFamilies(true);
      fetchAPI('/family-groups/with-members?limit=5', (data) => {
        setFamilyExamples(data || []);
        setLoadingFamilies(false);
      }, () => {
        setLoadingFamilies(false);
      });
    } else if (!hasFamilyQuota) {
      setFamilyExamples([]);
    }
  }, [variants.map(v => v.has_children_addon || v.has_adults_addon).join(',')]);

  // Reset state when form resets
  useEffect(() => {
    if (!createdQuote) {
      setVariants([]);
      setShowAddVariantForm(false);
      setEditingVariantIndex(null);
      setNewVariant({ nom: '', descripcio: '', import: '', has_children_addon: false, children_price: '', max_children: '', has_adults_addon: false, adult_price: '', max_adults: '' });
      setVariantValidationErrors({});
      setFamilyExamples([]);
      setCurrentStep(1);
    }
  }, [createdQuote]);

  // Sync form.value with first variant
  useEffect(() => {
    if (variants.length > 0 && variants[0].import !== '') {
      onChange('value', variants[0].import);
    }
  }, [variants]);

  // Initialize parent with default variant
  useEffect(() => {
    onChange('variants', variants);
  }, []);

  // Variant handlers
  const handleAddVariant = () => {
    const errors = validateVariant(newVariant);
    setVariantValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;
    
    const variantToAdd = {
      ...newVariant,
      import: parseFloat(newVariant.import),
      children_price: newVariant.has_children_addon ? parseFloat(newVariant.children_price) : null,
      max_children: newVariant.has_children_addon && newVariant.max_children ? parseInt(newVariant.max_children) : null,
      adult_price: newVariant.has_adults_addon ? parseFloat(newVariant.adult_price) : null,
      max_adults: newVariant.has_adults_addon && newVariant.max_adults ? parseInt(newVariant.max_adults) : null,
      display_order: variants.length,
      isDefault: false
    };
    const updatedVariants = [...variants.map(v => ({ ...v, isDefault: false })), variantToAdd];
    setVariants(updatedVariants);
    onChange('variants', updatedVariants);
    setNewVariant({ nom: '', descripcio: '', import: '', has_children_addon: false, children_price: '', max_children: '', has_adults_addon: false, adult_price: '', max_adults: '' });
    setVariantValidationErrors({});
    setShowAddVariantForm(false);
  };

  const handleUpdateVariant = (index) => {
    const variant = variants[index];
    const errors = validateVariant(variant);
    setVariantValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;
    
    const updatedVariant = {
      ...variant,
      import: parseFloat(variant.import),
      children_price: variant.has_children_addon ? parseFloat(variant.children_price) : null,
      max_children: variant.has_children_addon && variant.max_children ? parseInt(variant.max_children) : null,
      adult_price: variant.has_adults_addon ? parseFloat(variant.adult_price) : null,
      max_adults: variant.has_adults_addon && variant.max_adults ? parseInt(variant.max_adults) : null,
      isDefault: false
    };
    const updatedVariants = [...variants];
    updatedVariants[index] = updatedVariant;
    setVariants(updatedVariants);
    onChange('variants', updatedVariants);
    setVariantValidationErrors({});
    setEditingVariantIndex(null);
  };

  const handleDeleteVariant = (index) => {
    const updatedVariants = variants.filter((_, i) => i !== index);
    setVariants(updatedVariants);
    onChange('variants', updatedVariants);
  };

  const moveVariant = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= variants.length) return;
    const updatedVariants = [...variants];
    [updatedVariants[index], updatedVariants[targetIndex]] = [updatedVariants[targetIndex], updatedVariants[index]];
    setVariants(updatedVariants);
    onChange('variants', updatedVariants);
  };

  const updateVariantField = (index, field, value) => {
    const updatedVariants = [...variants];
    updatedVariants[index] = { ...updatedVariants[index], [field]: value };
    setVariants(updatedVariants);
  };

  // Calculate family cost
  const calculateFamilyCost = (variant, family) => {
    if (!family || !family.members || family.members.length === 0) return 0;
    const fixedPrice = parseFloat(variant.import) || 0;
    const adultPrice = parseFloat(variant.adult_price) || 0;
    const childrenPrice = parseFloat(variant.children_price) || 0;
    const adults = family.members.filter(m => !m.is_child).length;
    const children = family.members.filter(m => m.is_child).length;
    const effectiveAdults = variant.max_adults ? Math.min(adults, parseInt(variant.max_adults)) : adults;
    const effectiveChildren = variant.max_children ? Math.min(children, parseInt(variant.max_children)) : children;
    return fixedPrice + (effectiveAdults * adultPrice) + (effectiveChildren * childrenPrice);
  };

  // Step validation
  const canProceedFromStep = (step) => {
    switch (step) {
      case 1:
        return true; // Basic info always valid (title optional)
      case 2:
        // At least one variant with valid import
        const hasValidVariant = variants.some(v => 
          v.nom && v.nom.trim() !== '' && 
          v.import !== '' && !isNaN(parseFloat(v.import)) && parseFloat(v.import) >= 0
        );
        return hasValidVariant;
      case 3:
        return true; // Voluntària is optional
      case 4:
        return true; // Data obertura is optional
      case 5:
        return true; // Data límit is optional
      case 6:
        return true; // Comissió is optional
      default:
        return true;
    }
  };

  const goToNextStep = () => {
    if (currentStep < totalSteps && canProceedFromStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToPrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Styles


  // Render variant form
  const renderVariantForm = (variant, setVariant, onSave, onCancel, isNew = false) => {
    const isFamilyQuota = variant.has_children_addon || variant.has_adults_addon;
    
    return (
      <div>
        <div className="treasury-create-variantFormRow">
          <div>
            <label className="treasury-create-label">Nom de la variant</label>
            <input
              type="text"
              placeholder="Ex: Adult, Sènior 65+, Familiar..."
              value={variant.nom}
              onChange={e => {
                setVariant({ ...variant, nom: e.target.value });
                if (variantValidationErrors.nom) {
                  setVariantValidationErrors(prev => ({ ...prev, nom: null }));
                }
              }}
              className={['treasury-create-input', variantValidationErrors.nom ? 'treasury-create-inputError' : ''].filter(Boolean).join(' ')}
            />
            {variantValidationErrors.nom ? (
              <div className="treasury-create-fieldError">{variantValidationErrors.nom}</div>
            ) : null}
          </div>
          <div className="treasury-create-amount-field">
            <label className="treasury-create-label">{isFamilyQuota ? 'Import fixe' : 'Import'}</label>

            <div className="treasury-create-currencyWrapper">
              <input
                type="text"
                inputMode="decimal"
                placeholder={isFamilyQuota ? '0' : '25'}
                value={variant.import}
                onChange={e => {
                  const value = e.target.value;
                  if (/^\d*[.,]?\d*$/.test(value)) {
                    setVariant({ ...variant, import: value.replace(',', '.') });
                    if (variantValidationErrors.import) {
                      setVariantValidationErrors(prev => ({ ...prev, import: null }));
                    }
                  }
                }}
                className={['treasury-create-input', variantValidationErrors.import ? 'treasury-create-inputError' : ''].filter(Boolean).join(' ')} style={{ flex: 1 }}
              />
              <span className="treasury-create-currencySymbol">€</span>
            </div>
            {variantValidationErrors.import ? (
              <div className="treasury-create-fieldError">{variantValidationErrors.import}</div>
            ) : null}
          </div>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label className="treasury-create-label">Descripció (opcional)</label>
          <input
            type="text"
            placeholder="Ex: Només per gent de +65 anys"
            value={variant.descripcio || ''}
            onChange={e => setVariant({ ...variant, descripcio: e.target.value })}
            className="treasury-create-input"
          />
        </div>

        {hasFamiliesFeature() ? (
          <div
            className="treasury-create-familyToggle"
            onClick={() => {
              const isFamily = !isFamilyQuota;
              setVariant({
                ...variant,
                has_children_addon: isFamily,
                has_adults_addon: isFamily,
                import: isFamily && (!variant.import || variant.import === '') ? '0' : variant.import,
                children_price: isFamily ? (variant.children_price || '0') : '',
                max_children: isFamily ? variant.max_children : '',
                adult_price: isFamily ? (variant.adult_price || '0') : '',
                max_adults: isFamily ? variant.max_adults : ''
              });
            }}
          >
            <div className={['treasury-create-toggle', isFamilyQuota ? 'treasury-create-toggleActive' : ''].filter(Boolean).join(' ')}>
              <div className={['treasury-create-toggleKnob', isFamilyQuota ? 'treasury-create-toggleKnobActive' : ''].filter(Boolean).join(' ')} />
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>Quota familiar</div>
              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                Afegeix preus per adults i infants del nucli familiar
              </div>
            </div>
          </div>
        ) : null}

        {isFamilyQuota ? (
          <div className="treasury-create-familyPricing">
            <h4 className="treasury-create-familyPricingTitle">💰 Preus per membre familiar</h4>
            <div className="treasury-create-familyPricingRow">
              <div>
                <label className="treasury-create-label">Preu per adult</label>
                <div className="treasury-create-currencyWrapper">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0"
                    value={variant.adult_price}
                    onChange={e => {
                      const value = e.target.value;
                      if (/^\d*[.,]?\d*$/.test(value)) {
                        setVariant({ ...variant, adult_price: value.replace(',', '.'), has_adults_addon: true });
                      }
                    }}
                    className={['treasury-create-input'].filter(Boolean).join(' ')} style={{ flex: 1 }}
                  />
                  <span className="treasury-create-currencySymbol">€</span>
                </div>
              </div>
              <div>
                <label className="treasury-create-label">Màxim d'adults</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="∞"
                  value={variant.max_adults}
                  onChange={e => {
                    if (/^\d*$/.test(e.target.value)) {
                      setVariant({ ...variant, max_adults: e.target.value });
                    }
                  }}
                  className="treasury-create-input"
                />
                <span className="treasury-create-hint">Deixa buit = sense límit</span>
              </div>
            </div>
            <div className="treasury-create-familyPricingRow">
              <div>
                <label className="treasury-create-label">Preu per infant</label>
                <div className="treasury-create-currencyWrapper">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0"
                    value={variant.children_price}
                    onChange={e => {
                      const value = e.target.value;
                      if (/^\d*[.,]?\d*$/.test(value)) {
                        setVariant({ ...variant, children_price: value.replace(',', '.'), has_children_addon: true });
                      }
                    }}
                    className={['treasury-create-input'].filter(Boolean).join(' ')} style={{ flex: 1 }}
                  />
                  <span className="treasury-create-currencySymbol">€</span>
                </div>
              </div>
              <div>
                <label className="treasury-create-label">Màxim d'infants</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="∞"
                  value={variant.max_children}
                  onChange={e => {
                    if (/^\d*$/.test(e.target.value)) {
                      setVariant({ ...variant, max_children: e.target.value });
                    }
                  }}
                  className="treasury-create-input"
                />
                <span className="treasury-create-hint">Deixa buit = sense límit</span>
              </div>
            </div>

            {/* Family examples */}
            {familyExamples.length > 0 ? (
              <div className="treasury-create-familyExamples">
                <h5 className="treasury-create-familyExampleTitle">📊 Exemples de cost amb famílies reals:</h5>
                {familyExamples.slice(0, 3).map((family, idx) => {
                  const cost = calculateFamilyCost(variant, family);
                  const adults = family.members?.filter(m => !m.is_child).length || 0;
                  const children = family.members?.filter(m => m.is_child).length || 0;
                  return (
                    <div key={idx} className="treasury-create-familyExampleItem">
                      <div style={{ fontWeight: '600', color: '#166534', marginBottom: '4px', fontSize: '0.85rem' }}>
                        {family.nom || `Família ${idx + 1}`}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                        {adults} adult{adults !== 1 ? 's' : ''}, {children} infant{children !== 1 ? 's' : ''} → <strong>{formatMoney(cost)}€</strong>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        ) : null}

        <div style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '8px',
          marginTop: '16px',
          alignItems: 'center',
        }}>
          <button
            type="button"
            onClick={onSave}
            style={{
              padding: '8px 16px',
              background: '#3b82f6',
              color: 'white',
              border: '1px solid #2563eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.85rem',
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              display: 'inline-block',
              textAlign: 'center',
              lineHeight: '1.4',
              boxSizing: 'border-box',
            }}
          >
            ✓ Desar
          </button>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              background: 'white',
              color: '#64748b',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.85rem',
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              display: 'inline-block',
              textAlign: 'center',
              lineHeight: '1.4',
              boxSizing: 'border-box',
            }}
          >
            Cancel·lar
          </button>
        </div>
      </div>
    );
  };

  // Success view
  if (createdQuote) {
    return (
      <div className="treasury-create-container">
        <div className="treasury-create-successContainer">
          <div className="treasury-create-successIcon">✓</div>
          <h2 className="treasury-create-successTitle">Quota creada! 🎉</h2>
          <p className="treasury-create-successSubtitle">La nova quota ja està disponible per als membres.</p>
          <div className="treasury-create-successQuote">
            <p className="treasury-create-successQuoteText">
              {createdQuote.titol || getPeriodLabel(createdQuote.tipus, createdQuote.mes, createdQuote.any)} — {formatMoney(createdQuote.value)}€
            </p>
          </div>
          <p className={['treasury-create-successSubtitle'].filter(Boolean).join(' ')} style={{ marginBottom: '24px' }}>
            Vols enviar una notificació a tota la colla?
          </p>
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '12px',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <button
              type="button"
              disabled={sendingNotification}
              onClick={() => onSendNotification(createdQuote.id)}
              style={{
                padding: '10px 24px',
                background: '#3b82f6',
                color: 'white',
                border: '1px solid #2563eb',
                borderRadius: '6px',
                cursor: sendingNotification ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                fontSize: '0.9rem',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                display: 'inline-block',
                textAlign: 'center',
                lineHeight: '1.4',
                boxSizing: 'border-box',
                opacity: sendingNotification ? 0.5 : 1,
              }}
            >
              🔔 {sendingNotification ? 'Enviant...' : 'Notificar la colla'}
            </button>
            <button
              type="button"
              disabled={sendingNotification}
              onClick={onDismissSuccess}
              style={{
                padding: '10px 20px',
                background: '#f1f5f9',
                color: '#475569',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: sendingNotification ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                fontSize: '0.9rem',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                display: 'inline-block',
                textAlign: 'center',
                lineHeight: '1.4',
                boxSizing: 'border-box',
                opacity: sendingNotification ? 0.5 : 1,
              }}
            >
              No enviar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step labels - dynamic based on Stripe status
  const stepLabels = stripeConnectStatus?.verified 
    ? ['Tipus', 'Preu', 'Voluntària', 'Obertura', 'Límit', 'Comissió']
    : ['Tipus', 'Preu', 'Voluntària', 'Obertura', 'Límit'];

  return (
    <div className="treasury-create-container">
      {/* Header with progress */}
      <div className="treasury-create-header">
        <h2 className="treasury-create-title">Nova quota</h2>
        <p className="treasury-create-subtitle">Configura una nova quota en {totalSteps} passos</p>
        
        <div className="treasury-create-mobile-progress">
          <p role="status">Pas {currentStep} de {totalSteps} · {stepLabels[currentStep - 1]}</p>
          <progress aria-label="Progrés de la quota" value={currentStep} max={totalSteps} />
        </div>
        <div className="treasury-create-progressContainer">
          {Array.from({ length: totalSteps }, (_, idx) => idx + 1).map((step, idx) => (
            <React.Fragment key={step}>
              {idx > 0 ? (
                <div className={['treasury-create-progressLine', currentStep > idx ? 'treasury-create-progressLineCompleted' : ''].filter(Boolean).join(' ')} />
              ) : null}
              <div style={{ textAlign: 'center' }}>
                <div className={['treasury-create-progressDot', currentStep === step ? 'treasury-create-progressDotActive' :
                      currentStep > step ? 'treasury-create-progressDotCompleted' : 'treasury-create-progressDotPending'].filter(Boolean).join(' ')}>
                  {currentStep > step ? '✓' : step}
                </div>
                <div className="treasury-create-stepLabel">{stepLabels[idx]}</div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="treasury-create-body">
        <form onSubmit={onSubmit} style={{ display: 'inline-block', width: '100%' }}>
          {/* Step 1: Type */}
          {currentStep === 1 ? (
            <div>
              <h3 className="treasury-create-stepTitle">Quin tipus de quota és?</h3>
              <p className="treasury-create-stepDescription">
                Tria si és una quota puntual (un sol cobrament) o recurrent (amb subscripció Stripe).
              </p>

              <div className="treasury-create-typeSelector">
                <div 
                  className={['treasury-create-typeCard', !form.is_subscription ? 'treasury-create-typeCardSelected' : ''].filter(Boolean).join(' ')}
                  onClick={() => onChange('is_subscription', false)}
                >
                  <div className="treasury-create-typeIcon">📅</div>
                  <div className="treasury-create-typeName">Quota puntual</div>
                  <div className="treasury-create-typeDesc">Un sol cobrament per un període concret</div>
                </div>
                <div 
                  className={['treasury-create-typeCard', form.is_subscription ? 'treasury-create-typeCardSelected' : ''].filter(Boolean).join(' ')}
                  onClick={() => onChange('is_subscription', true)}
                >
                  <div className="treasury-create-typeIcon">🔄</div>
                  <div className="treasury-create-typeName">Subscripció</div>
                  <div className="treasury-create-typeDesc">Cobrament automàtic recurrent via Stripe</div>
                </div>
              </div>

              <div className="treasury-create-formGroup">
                <label className="treasury-create-label">Títol</label>
                <input
                  type="text"
                  placeholder="Ex: Quota anual 2026, Aportació extraordinària..."
                  value={form.titol}
                  onChange={(e) => onChange('titol', e.target.value)}
                  className="treasury-create-input"
                />
              </div>

              {form.is_subscription ? (
                <div className="treasury-create-formGroup">
                  <label className="treasury-create-label">Freqüència de cobrament</label>
                  <select
                    value={form.cadence || 'monthly'}
                    onChange={(e) => onChange('cadence', e.target.value)}
                    className="treasury-create-select"
                  >
                    <option value="monthly">Mensual</option>
                    <option value="quarterly">Trimestral</option>
                    <option value="semiannual">Semestral</option>
                    <option value="annual">Anual</option>
                  </select>
                </div>
              ) : (
                <>
                  <div className="treasury-create-period-grid">
                    <div className="treasury-create-formGroup">
                      <label className="treasury-create-label">Tipus de període</label>
                      <select
                        value={form.tipus}
                        onChange={(e) => onChange('tipus', e.target.value)}
                        className="treasury-create-select"
                      >
                        <option value="anual">Anual</option>
                        <option value="semestral">Semestral</option>
                        <option value="quadrimestral">Quadrimestral</option>
                        <option value="trimestral">Trimestral</option>
                      </select>
                    </div>
                    <div className="treasury-create-formGroup">
                      <label className="treasury-create-label">Any</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={form.any}
                        onChange={(e) => {
                          if (/^\d*$/.test(e.target.value)) {
                            onChange('any', e.target.value);
                          }
                        }}
                        className="treasury-create-input"
                      />
                    </div>
                  </div>
                  {form.tipus !== 'anual' ? (
                    <div className="treasury-create-formGroup">
                      <label className="treasury-create-label">Període</label>
                      <select
                        value={form.mes}
                        onChange={(e) => onChange('mes', e.target.value)}
                        className="treasury-create-select"
                      >
                        {getAvailableMonths().map((month) => (
                          <option key={month} value={month}>
                            {form.tipus === 'trimestral'
                              ? `T${Math.ceil(month / 3)} (${monthNames[month - 1]})`
                              : form.tipus === 'semestral'
                              ? `S${Math.ceil(month / 6)} (${monthNames[month - 1]})`
                              : `Q${Math.ceil(month / 4)} (${monthNames[month - 1]})`}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          ) : null}

          {/* Step 2: Pricing */}
          {currentStep === 2 ? (
            <div>
              <h3 className="treasury-create-stepTitle">Defineix el preu</h3>
              <p className="treasury-create-stepDescription">
                Afegeix les variants de preu de la quota. Pots tenir una variant simple o múltiples opcions.
              </p>

              {variants.map((variant, index) => (
                <div 
                  key={index} 
                  className={['treasury-create-variantCard', editingVariantIndex === index ? 'treasury-create-variantCardEditing' : ''].filter(Boolean).join(' ')}
                >
                  {editingVariantIndex === index ? (
                    renderVariantForm(
                      variants[index],
                      (updatedVariant) => {
                        const newVariants = [...variants];
                        newVariants[index] = updatedVariant;
                        setVariants(newVariants);
                      },
                      () => handleUpdateVariant(index),
                      () => {
                        setEditingVariantIndex(null);
                        setVariantValidationErrors({});
                      }
                    )
                  ) : (
                    <>
                      <div className="treasury-create-variantHeader">
                        <div>
                          <span className="treasury-create-variantName">{variant.nom}</span>
                          {variant.descripcio ? (
                            <span style={{ fontSize: '0.8rem', color: '#64748b', marginLeft: '8px' }}>{variant.descripcio}</span>
                          ) : null}
                          <span className="treasury-create-variantPrice"> — {formatMoney(variant.import)}€</span>
                          {variant.has_adults_addon ? (
                            <span className="treasury-create-variantBadge">
                              +{formatMoney(variant.adult_price)}€/adult
                            </span>
                          ) : null}
                          {variant.has_children_addon ? (
                            <span className="treasury-create-variantBadge">
                              +{formatMoney(variant.children_price)}€/infant
                            </span>
                          ) : null}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'row', gap: '4px', alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={() => moveVariant(index, -1)}
                            disabled={index === 0}
                            style={{
                              width: '32px',
                              height: '32px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'white',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              cursor: index === 0 ? 'not-allowed' : 'pointer',
                              fontSize: '0.9rem',
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                              opacity: index === 0 ? 0.4 : 1,
                              padding: 0,
                              lineHeight: 1,
                              boxSizing: 'border-box',
                              color: '#2563eb',
                            }}
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            onClick={() => moveVariant(index, 1)}
                            disabled={index === variants.length - 1}
                            style={{
                              width: '32px',
                              height: '32px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'white',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              cursor: index === variants.length - 1 ? 'not-allowed' : 'pointer',
                              fontSize: '0.9rem',
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                              opacity: index === variants.length - 1 ? 0.4 : 1,
                              padding: 0,
                              lineHeight: 1,
                              boxSizing: 'border-box',
                              color: '#2563eb',
                            }}
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingVariantIndex(index);
                              setVariantValidationErrors({});
                            }}
                            style={{
                              width: '32px',
                              height: '32px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'white',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                              padding: 0,
                              lineHeight: 1,
                              boxSizing: 'border-box',
                            }}
                          >
                            ✏️
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteVariant(index)}
                            style={{
                              width: '32px',
                              height: '32px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'white',
                              border: '1px solid #fee2e2',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              color: '#ef4444',
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                              padding: 0,
                              lineHeight: 1,
                              boxSizing: 'border-box',
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}

              {variants.length === 0 && !showAddVariantForm ? (
                <div 
                  onClick={() => setShowAddVariantForm(true)}
                  style={{
                    padding: '40px 24px',
                    background: '#f8fafc',
                    border: '2px dashed #cbd5e1',
                    borderRadius: '12px',
                    textAlign: 'center',
                    marginBottom: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '16px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#94a3b8';
                    e.currentTarget.style.background = '#f1f5f9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#cbd5e1';
                    e.currentTarget.style.background = '#f8fafc';
                  }}
                >
                  <div style={{ 
                    width: '64px', 
                    height: '64px', 
                    background: '#e2e8f0', 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '2rem',
                    color: '#64748b'
                  }}>
                    🏷️
                  </div>
                  <div>
                    <h4 style={{ margin: '0 0 8px', color: '#334155', fontSize: '1.1rem', fontWeight: '600' }}>
                      Defineix els preus
                    </h4>
                    <p style={{ margin: 0, color: '#64748b', fontSize: '0.95rem', maxWidth: '400px' }}>
                      Afegeix variants per a diferents tipus de socis (ex: Adults, Infants, Famílies).
                    </p>
                  </div>
                  <button
                    type="button"
                    style={{
                      marginTop: '8px',
                      padding: '10px 24px',
                      background: '#3b82f6',
                      color: 'white',
                      border: '1px solid #2563eb',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      fontSize: '0.9rem',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      display: 'inline-block',
                    }}
                  >
                    ➕ Afegir primera variant
                  </button>
                </div>
              ) : null}

              {showAddVariantForm ? (
                <div className={['treasury-create-variantCard', 'treasury-create-variantCardEditing'].filter(Boolean).join(' ')}>
                  <h4 style={{ margin: '0 0 16px', fontSize: '0.95rem', color: '#334155' }}>Nova variant</h4>
                  {renderVariantForm(
                    newVariant,
                    setNewVariant,
                    handleAddVariant,
                    () => {
                      setShowAddVariantForm(false);
                      setNewVariant({ nom: '', descripcio: '', import: '', has_children_addon: false, children_price: '', max_children: '', has_adults_addon: false, adult_price: '', max_adults: '' });
                      setVariantValidationErrors({});
                    },
                    true
                  )}
                </div>
              ) : (
                variants.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setShowAddVariantForm(true)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#e0f2fe', // blau clar
                      border: '1.5px dashed #2563eb', // blau fort
                      borderRadius: '8px',
                      cursor: 'pointer',
                      color: '#2563eb',
                      fontWeight: '500',
                      fontSize: '0.9rem',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      textAlign: 'center',
                      lineHeight: '1.4',
                      boxSizing: 'border-box',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = '#94a3b8';
                      e.target.style.color = '#475569';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = '#cbd5e1';
                      e.target.style.color = '#64748b';
                    }}
                  >
                    ➕ Afegir una altra variant
                  </button>
                ) : null
              )}
            </div>
          ) : null}

          {/* Step 3: Voluntària */}
          {currentStep === 3 ? (
            <div>
              <h3 className="treasury-create-stepTitle">Quota voluntària?</h3>
              <p className="treasury-create-stepDescription">
                Les quotes voluntàries permeten als usuaris posposar el pagament.
              </p>

              <div 
                className={['treasury-create-optionCard', !form.voluntaria ? 'treasury-create-optionCardSelected' : ''].filter(Boolean).join(' ')}
                onClick={() => onChange('voluntaria', false)}
              >
                <div className="treasury-create-optionHeader">
                  <div className={['treasury-create-checkbox', !form.voluntaria ? 'treasury-create-checkboxChecked' : ''].filter(Boolean).join(' ')}>
                    {!form.voluntaria ? <span style={{ color: 'white', fontSize: '0.8rem' }}>✓</span> : null}
                  </div>
                  <h4 className="treasury-create-optionTitle">Quota obligatòria</h4>
                </div>
                <p className="treasury-create-optionDesc">
                  Els usuaris han de pagar la quota quan se'ls presenta.
                </p>
              </div>

              <div 
                className={['treasury-create-optionCard', form.voluntaria ? 'treasury-create-optionCardSelected' : ''].filter(Boolean).join(' ')}
                onClick={() => onChange('voluntaria', true)}
              >
                <div className="treasury-create-optionHeader">
                  <div className={['treasury-create-checkbox', form.voluntaria ? 'treasury-create-checkboxChecked' : ''].filter(Boolean).join(' ')}>
                    {form.voluntaria ? <span style={{ color: 'white', fontSize: '0.8rem' }}>✓</span> : null}
                  </div>
                  <h4 className="treasury-create-optionTitle">Quota voluntària</h4>
                </div>
                <p className="treasury-create-optionDesc">
                  Permet als usuaris dir "No pagar ara". Podran pagar més tard a l'apartat de quotes pendents.
                </p>
              </div>
            </div>
          ) : null}

          {/* Step 4: Data d'obertura */}
          {currentStep === 4 ? (
            <div>
              <h3 className="treasury-create-stepTitle">Data d'obertura</h3>
              <p className="treasury-create-stepDescription">
                Quan vols que la quota sigui visible per als membres?
              </p>

              <div 
                className={['treasury-create-optionCard', !form.data_obertura ? 'treasury-create-optionCardSelected' : ''].filter(Boolean).join(' ')}
                onClick={() => onChange('data_obertura', '')}
              >
                <div className="treasury-create-optionHeader">
                  <div className={['treasury-create-checkbox', !form.data_obertura ? 'treasury-create-checkboxChecked' : ''].filter(Boolean).join(' ')}>
                    {!form.data_obertura ? <span style={{ color: 'white', fontSize: '0.8rem' }}>✓</span> : null}
                  </div>
                  <h4 className="treasury-create-optionTitle">Visible immediatament</h4>
                </div>
                <p className="treasury-create-optionDesc">
                  La quota serà visible per a tothom un cop creada.
                </p>
              </div>

              <div 
                className={['treasury-create-optionCard', form.data_obertura ? 'treasury-create-optionCardSelected' : ''].filter(Boolean).join(' ')}
                onClick={() => {
                  if (!form.data_obertura) {
                    // Set default to tomorrow at 9:00
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    tomorrow.setHours(9, 0, 0, 0);
                    // Format as local time (YYYY-MM-DDTHH:MM) - don't use toISOString() as it converts to UTC
                    const formatted = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}T${String(tomorrow.getHours()).padStart(2, '0')}:${String(tomorrow.getMinutes()).padStart(2, '0')}`;
                    onChange('data_obertura', formatted);
                  }
                }}
              >
                <div className="treasury-create-optionHeader">
                  <div className={['treasury-create-checkbox', form.data_obertura ? 'treasury-create-checkboxChecked' : ''].filter(Boolean).join(' ')}>
                    {form.data_obertura ? <span style={{ color: 'white', fontSize: '0.8rem' }}>✓</span> : null}
                  </div>
                  <h4 className="treasury-create-optionTitle">Programar obertura</h4>
                </div>
                <p className="treasury-create-optionDesc">
                  La quota només serà visible a partir d'una data concreta.
                </p>
              </div>

              {form.data_obertura ? (
                <div style={{ marginTop: '16px' }}>
                  <label className="treasury-create-label">Data i hora d'obertura</label>
                  <input
                    type="datetime-local"
                    value={form.data_obertura}
                    onChange={(e) => onChange('data_obertura', e.target.value)}
                    className={['treasury-create-input'].filter(Boolean).join(' ')} style={{ maxWidth: '300px' }}
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Step 5: Data límit */}
          {currentStep === 5 ? (
            <div>
              <h3 className="treasury-create-stepTitle">Data límit de pagament</h3>
              <p className="treasury-create-stepDescription">
                Vols establir una data límit per pagar la quota?
              </p>

              <div 
                className={['treasury-create-optionCard', !form.data_limit ? 'treasury-create-optionCardSelected' : ''].filter(Boolean).join(' ')}
                onClick={() => onChange('data_limit', '')}
              >
                <div className="treasury-create-optionHeader">
                  <div className={['treasury-create-checkbox', !form.data_limit ? 'treasury-create-checkboxChecked' : ''].filter(Boolean).join(' ')}>
                    {!form.data_limit ? <span style={{ color: 'white', fontSize: '0.8rem' }}>✓</span> : null}
                  </div>
                  <h4 className="treasury-create-optionTitle">Sense data límit</h4>
                </div>
                <p className="treasury-create-optionDesc">
                  Els usuaris podran pagar la quota en qualsevol moment.
                </p>
              </div>

              <div 
                className={['treasury-create-optionCard', form.data_limit ? 'treasury-create-optionCardSelected' : ''].filter(Boolean).join(' ')}
                onClick={() => {
                  if (!form.data_limit) {
                    // Set default to end of next month at 23:59
                    const nextMonth = new Date();
                    nextMonth.setMonth(nextMonth.getMonth() + 1);
                    nextMonth.setDate(1);
                    nextMonth.setDate(0); // Last day of current month + 1 = last day of next month
                    nextMonth.setHours(23, 59, 0, 0);
                    // Format as local time (YYYY-MM-DDTHH:MM) - don't use toISOString() as it converts to UTC
                    const formatted = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}-${String(nextMonth.getDate()).padStart(2, '0')}T${String(nextMonth.getHours()).padStart(2, '0')}:${String(nextMonth.getMinutes()).padStart(2, '0')}`;
                    onChange('data_limit', formatted);
                  }
                }}
              >
                <div className="treasury-create-optionHeader">
                  <div className={['treasury-create-checkbox', form.data_limit ? 'treasury-create-checkboxChecked' : ''].filter(Boolean).join(' ')}>
                    {form.data_limit ? <span style={{ color: 'white', fontSize: '0.8rem' }}>✓</span> : null}
                  </div>
                  <h4 className="treasury-create-optionTitle">Establir data límit</h4>
                </div>
                <p className="treasury-create-optionDesc">
                  Un cop passada la data, els usuaris ja no podran pagar la quota.
                </p>
              </div>

              {form.data_limit ? (
                <div style={{ marginTop: '16px' }}>
                  <label className="treasury-create-label">Data i hora límit</label>
                  <input
                    type="datetime-local"
                    value={form.data_limit}
                    onChange={(e) => onChange('data_limit', e.target.value)}
                    className={['treasury-create-input'].filter(Boolean).join(' ')} style={{ maxWidth: '300px' }}
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Step 6: Comissió Targeta (only if Stripe verified) */}
          {currentStep === 6 && stripeConnectStatus?.verified ? (
            <div>
              <h3 className="treasury-create-stepTitle">Comissió Targeta</h3>
              <p className="treasury-create-stepDescription">
                Qui paga la comissió del 4% + 0,3€ per cada pagament amb targeta?
              </p>

              <div 
                className={['treasury-create-optionCard', form.commission_mode !== 'colla_absorbs' ? 'treasury-create-optionCardSelected' : ''].filter(Boolean).join(' ')}
                onClick={() => onChange('commission_mode', 'user_pays')}
              >
                <div className="treasury-create-optionHeader">
                  <div className={['treasury-create-checkbox', form.commission_mode !== 'colla_absorbs' ? 'treasury-create-checkboxChecked' : ''].filter(Boolean).join(' ')}>
                    {form.commission_mode !== 'colla_absorbs' ? <span style={{ color: 'white', fontSize: '0.8rem' }}>✓</span> : null}
                  </div>
                  <h4 className="treasury-create-optionTitle">L'usuari paga la comissió</h4>
                </div>
                <p className="treasury-create-optionDesc">
                  Import final = quota + comissió. La colla rep l'import íntegre de la quota.
                </p>
              </div>

              <div 
                className={['treasury-create-optionCard', form.commission_mode === 'colla_absorbs' ? 'treasury-create-optionCardSelected' : ''].filter(Boolean).join(' ')}
                onClick={() => onChange('commission_mode', 'colla_absorbs')}
              >
                <div className="treasury-create-optionHeader">
                  <div className={['treasury-create-checkbox', form.commission_mode === 'colla_absorbs' ? 'treasury-create-checkboxChecked' : ''].filter(Boolean).join(' ')}>
                    {form.commission_mode === 'colla_absorbs' ? <span style={{ color: 'white', fontSize: '0.8rem' }}>✓</span> : null}
                  </div>
                  <h4 className="treasury-create-optionTitle">La colla absorbeix la comissió</h4>
                </div>
                <p className="treasury-create-optionDesc">
                  L'usuari paga només l'import de la quota. La colla rep menys per cada pagament.
                </p>
              </div>
            </div>
          ) : null}

          {/* Navigation */}
<div className="treasury-create-footer">
            {currentStep > 1 ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goToPrevStep();
                }}
                style={{
                  padding: '10px 20px',
                  background: '#f1f5f9',
                  color: '#475569',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.9rem',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  display: 'inline-block',
                  textAlign: 'center',
                  lineHeight: '1.4',
                  boxSizing: 'border-box',
                }}
              >
                ← Enrere
              </button>
            ) : (
              <div style={{ flex: 1 }} />
            )}
            
            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goToNextStep();
                }}
                disabled={!canProceedFromStep(currentStep)}
                style={{
                  padding: '10px 24px',
                  background: canProceedFromStep(currentStep) ? '#3b82f6' : '#94a3b8',
                  color: 'white',
                  border: '1px solid ' + (canProceedFromStep(currentStep) ? '#2563eb' : '#94a3b8'),
                  borderRadius: '6px',
                  cursor: canProceedFromStep(currentStep) ? 'pointer' : 'not-allowed',
                  fontWeight: '500',
                  fontSize: '0.9rem',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  display: 'inline-block',
                  textAlign: 'center',
                  lineHeight: '1.4',
                  boxSizing: 'border-box',
                  opacity: canProceedFromStep(currentStep) ? 1 : 0.5,
                }}
              >
                Continuar →
              </button>
            ) : (
              <button
                type="submit"
                style={{
                  padding: '10px 24px',
                  background: '#3b82f6',
                  color: 'white',
                  border: '1px solid #2563eb',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.9rem',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  display: 'inline-block',
                  textAlign: 'center',
                  lineHeight: '1.4',
                  boxSizing: 'border-box',
                }}
              >
                ✓ Crear quota
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateQuoteForm;
